import React from 'react';
import { Button } from './button';

export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface EnhancedPaginationProps {
  pagination: PaginationInfo;
  currentPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  isLoading?: boolean;
  className?: string;
}

export function EnhancedPagination({
  pagination,
  currentPage,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  className = "",
}: EnhancedPaginationProps) {
  const { totalPages, totalCount, pageSize } = pagination;
  
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageSizeChange = (value: string) => {
    const newPageSize = value === 'all' ? totalCount : parseInt(value);
    onPageSizeChange(newPageSize);
    // Reset to first page when changing page size
    onPageChange(1);
  };

  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * Math.min(pageSize, totalCount) + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className={`flex items-center justify-between border-t pt-4 ${className}`}>
      <div className="flex items-center space-x-4">
        <div className="text-sm text-gray-600">
          Showing {startItem} to {endItem} of {totalCount} items
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Show:</span>
          <select
            value={pageSize >= totalCount ? 'all' : pageSize.toString()}
            onChange={(e) => handlePageSizeChange(e.target.value)}
            disabled={isLoading}
            className="w-20 h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {totalPages > 1 && pageSize < totalCount && (
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || isLoading}
            className="h-8"
          >
            Previous
          </Button>
          
          <div className="flex items-center space-x-1">
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage >= totalPages || isLoading}
            className="h-8"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}